import * as React from "react";
import { Trans, useTranslation } from "react-i18next";
import styled, { css } from "styled-components";
import { Field, useField } from "formik";
import { useQuery } from "@tanstack/react-query";
import trim from "lodash/trim";
import { Col, FormControl, FormGroup, Panel, Row } from "components/bootstrap";
import DocumentationLink from "components/support/DocumentationLink";
import DocsHelper from "util/DocsHelper";
import { testNaturalDate } from "api/tools";
import useUserDateTime from "hooks/useUserDateTime";
import { InputDescription, Timestamp } from "components/common";
import debounceWithPromise from "views/logic/debounceWithPromise";
import { EMPTY_RANGE } from "views/components/time-range-picker/TimeRangeDisplay";
const Headline = styled.h3`
  margin-bottom: 5px;
`;
const KeywordInput = styled(FormControl)(
  ({ theme }) => css`
    min-height: 34px;
    font-size: ${theme.fonts.size.large};
  `
);
const EffectiveTimeRangeTable = styled.table`
  margin-bottom: 5px;

  td:first-child {
    padding-right: 10px;
  }
`;
const debouncedTestNaturalDate = debounceWithPromise(
  (keyword, userTZ) => testNaturalDate(keyword, userTZ),
  350
);
const TimePreview = ({ dateTime, isLoading }) => {
  if (!dateTime || isLoading) {
    return /* @__PURE__ */ React.createElement(React.Fragment, null, EMPTY_RANGE);
  }
  return /* @__PURE__ */ React.createElement(Timestamp, { dateTime, format: "complete" });
};
const useKeywordPreview = (keyword, userTZ) => {
  const { data, isFetching } = useQuery({
    queryKey: ["time-range", "validation", "keyword", keyword],
    queryFn: () => debouncedTestNaturalDate(keyword, userTZ),
    retry: 0,
    enabled: !!trim(keyword)
  });
  return { data, isFetching };
};
const KeywordTimeRangePreview = () => {
  const { t } = useTranslation("search");
  const [
    {
      value: { keyword }
    }
  ] = useField("timeRangeTabs.keyword");
  const { userTimezone } = useUserDateTime();
  const { data, isFetching } = useKeywordPreview(keyword, userTimezone);
  return /* @__PURE__ */ React.createElement(EffectiveTimeRangeTable, null, /* @__PURE__ */ React.createElement("tbody", null, /* @__PURE__ */ React.createElement("tr", null, /* @__PURE__ */ React.createElement("td", null, t("time_range.keyword_from")), /* @__PURE__ */ React.createElement("td", null, /* @__PURE__ */ React.createElement(TimePreview, { dateTime: data == null ? void 0 : data.from, isLoading: isFetching }))), /* @__PURE__ */ React.createElement("tr", null, /* @__PURE__ */ React.createElement("td", null, t("time_range.keyword_to")), /* @__PURE__ */ React.createElement("td", null, /* @__PURE__ */ React.createElement(TimePreview, { dateTime: data == null ? void 0 : data.to, isLoading: isFetching })))));
};
const KEYWORD_PLACEHOLDER = "Last week";
const KEYWORD_EXAMPLES = [
  { keyword: "last month", description: "time_range.keyword_example_last_month" },
  { keyword: "4 hours ago", description: "time_range.keyword_example_hours_ago" },
  { keyword: "1st of april to 2 days ago", description: "time_range.keyword_example_range" },
  { keyword: "yesterday midnight +0200 to today midnight +0200", description: "time_range.keyword_example_timezone" }
];
const KeywordDocumentationLink = ({ children = void 0 }) => {
  const { t } = useTranslation("search");
  return /* @__PURE__ */ React.createElement(
    DocumentationLink,
    {
      page: DocsHelper.PAGES.TIME_FRAME_SELECTOR,
      title: t("time_range.keyword_docs_title"),
      text: children
    }
  );
};
const TabKeywordTimeRange = ({ disabled = false }) => {
  const { t } = useTranslation("search");
  return /* @__PURE__ */ React.createElement(Row, { className: "no-bm" }, /* @__PURE__ */ React.createElement(Col, { sm: 5 }, /* @__PURE__ */ React.createElement(Headline, null, t("time_range.keyword_headline")), /* @__PURE__ */ React.createElement(Field, { name: "timeRangeTabs.keyword.keyword" }, ({ field: { name, value, onChange }, meta: { error } }) => /* @__PURE__ */ React.createElement(
    FormGroup,
    {
      controlId: "form-inline-keyword",
      style: { marginRight: 5, width: "100%", marginBottom: 0 },
      validationState: error ? "error" : null
    },
    /* @__PURE__ */ React.createElement(
      KeywordInput,
      {
        type: "text",
        className: "input-sm mousetrap",
        name,
        disabled,
        placeholder: KEYWORD_PLACEHOLDER,
        title: t("time_range.keyword_input"),
        "aria-label": t("time_range.keyword_input"),
        onChange,
        required: true,
        value
      }
    ),
    /* @__PURE__ */ React.createElement(InputDescription, { error, help: t("time_range.keyword_help") })
  )), /* @__PURE__ */ React.createElement("b", null, t("time_range.keyword_preview")), /* @__PURE__ */ React.createElement(KeywordTimeRangePreview, null)), /* @__PURE__ */ React.createElement(Col, { sm: 7 }, /* @__PURE__ */ React.createElement(Panel, null, /* @__PURE__ */ React.createElement(Panel.Body, null, KEYWORD_EXAMPLES.map(({ keyword, description }) => /* @__PURE__ */ React.createElement("p", { key: keyword }, /* @__PURE__ */ React.createElement(Trans, { t, i18nKey: description, values: { keyword }, components: { code: /* @__PURE__ */ React.createElement("code", null) } }))), /* @__PURE__ */ React.createElement("p", null, /* @__PURE__ */ React.createElement(Trans, { t, i18nKey: "time_range.keyword_docs", components: { docsLink: /* @__PURE__ */ React.createElement(KeywordDocumentationLink, null) } }))))));
};
export default TabKeywordTimeRange;
